import * as Dialog from "../framework/dialog";
import { getUITranslation } from "../utils/get_ui_translation";
import { createProfilePicture } from "./profile_picture";

/* global _money */

export function versionnedStorageKey(key, version) {
  localStorage.removeItem(key);
  for (var i = 0; i < version; i++) {
    localStorage.removeItem(key + `_version${i}`);
  }
  return key + `_version${version}`;
}

export function createColumns(columnsFields, columnsLogics, columnsVisible = "all", columnsKept = "all", columnsHeaderFilters = []) {
  return columnsFields
    .reduce((columns, field) => {
      if (columnsKept !== "all" && !columnsKept.find((it) => it == field.dataField)) return columns;
      let columnLogic = columnsLogics.find((logic) => logic.dataField == field.dataField) || {};
      return [
        ...columns,
        {
          ...field,
          ...columnLogic,
          visible: columnsVisible === "all" || columnsVisible.find((it) => it == field.dataField) !== undefined,
          allowHeaderFiltering:
            columnsHeaderFilters === "all" || columnsHeaderFilters.find((it) => it == field.dataField) !== undefined,
        },
      ];
    }, [])
    .sort((a, b) => a.caption.localeCompare(b.caption));
}

export function createColumnsWithSubColumn(columns, columnsLogics) {
  const fieldsColumns = columns[0];
  const detailColumns = createColumns(fieldsColumns.columns, columnsLogics);
  return [{ caption: fieldsColumns.caption, columns: detailColumns }];
}

/**
 * Add a prefix to the keys of column overrides.
 *
 * This function is used to handle the transition from prefixed to unprefixed keys,
 * when moving from batch_loading to server_side loading mode.
 *
 * @param {Object} columnOverrides
 * @param {string} prefix
 *
 * @returns {Array<Object>}
 */
export function prefixColumnOverrides(columnOverrides, prefix) {
  if (prefix === "") return columnOverrides;

  let columns = { ...columnOverrides };

  for (const baseKey in columns) {
    if (!Object.hasOwn(columns, baseKey)) continue;

    const newKey = prefix + baseKey;
    columns[newKey] = columnOverrides[baseKey];
    delete columns[baseKey];
  }

  return columns;
}

/**
 * Merge two column overrides. The second overrides has priority over the first.
 *
 * If an override in the second object is set to null, the key will deleted
 * from the merged result.
 *
 * @param {{String: Object}} overrides1
 * @param {{String: Object | null}} overrides2
 *
 * @returns {{String: Object}}
 */
export function mergeColumnOverrides(overrides1, overrides2) {
  let merged = { ...overrides1 };

  for (const key in overrides2) {
    if (!Object.hasOwn(overrides2, key)) continue;

    if (overrides2[key] === null) {
      delete merged[key];
    } else if (!Object.hasOwn(merged, key)) {
      merged[key] = overrides2[key];
    } else {
      merged[key] = {
        ...merged[key],
        ...overrides2[key],
      };
    }
  }

  return merged;
}

/**
 * Keep only the keys of the column overrides that are in the columnsKept array.
 *
 * This is used to avoid having overrides for columns that are not in the DataGrid,
 * which causes warnings in the console.
 *
 * @param {{String: Object}} overrides
 * @param {[String]} columnsKept
 *
 * @returns {{String: Object}}
 */
export function keepColumnOverrides(overrides, columnsKept) {
  let overridesKept = Object.create(null);

  for (const key of columnsKept) {
    if (Object.hasOwn(overrides, key)) {
      overridesKept[key] = overrides[key];
    }
  }

  return overridesKept;
}

export const currencyFormat = () => ({ type: "currency", currency: _money.code, precision: 2 });

export const numberFormat = () => ({ type: "#,##0.##" });

/**
 * You need profile picture in your devextrem ? I got you
 */
export function makePersonLogoCellTemplate({ idField, firstnameField, lastnameField, alt = "" }) {
  return (container, options) => {
    const data = options.row.data;
    const firstname = data[firstnameField] ?? "";
    const lastname = data[lastnameField] ?? "";
    container.appendChild(
      createProfilePicture({
        src: options.displayValue,
        id: data[idField] ?? 0,
        initials: `${lastname[0] ?? ""}${firstname[0] ?? ""}`,
        alt,
      })
    );
  };
}

export function urlCellTemplate(url, text, container, attributes = {}) {
  const newCell = document.createElement("a");
  newCell.classList.add("anchor");
  newCell.textContent = text;
  newCell.setAttribute("href", url);
  for (const key in attributes) {
    newCell.setAttribute(key, attributes[key]);
  }
  container.appendChild(newCell);
}

/**
 * You need badge in your devextrem ? I got you
 *
 * @param {Parent element} container
 * @param {Displayed text} text
 * @param {Additional attributes} attributes
 */
export function badgeCellTemplate(container, text, attributes = {}) {
  const newCell = document.createElement("span");
  newCell.textContent = text;
  for (const key in attributes) {
    newCell.setAttribute(key, attributes[key]);
  }
  newCell.classList.add("badge");
  container.appendChild(newCell);
  return newCell;
}

/**
 * You need fomo in your devextrem ? I got you
 *
 * @param {Parent element} container
 * @param {Displayed text} text
 * @param {Redirection url} url
 * @param {Additional attributes} attributes
 */
export function fomoCellTemplate(container, text, url, attributes = {}) {
  const newCell = document.createElement("a");
  newCell.classList.add("anchor");
  newCell.textContent = text;
  newCell.setAttribute("href", url);
  newCell.setAttribute("target", "_blank");
  for (const key in attributes) {
    newCell.setAttribute(key, attributes[key]);
  }
  newCell.classList.add("badge");
  newCell.classList.add("badge--fomo");
  container.appendChild(newCell);
}

export function urlBadgeTemplate(container, text, color = null, size = null, attributes = {}, child = null) {
  const newCell = document.createElement("span");
  newCell.classList.add("badge");
  if (size) newCell.classList.add(`badge--${size}`);
  if (color) newCell.classList.add(color);
  newCell.textContent = text;
  if (child) newCell.appendChild(child);
  for (const key in attributes) {
    newCell.setAttribute(key, attributes[key]);
  }
  container.appendChild(newCell);
}

export function addResetToColumnChooser(storageKey) {
  /*
    Add an undo button to the column chooser popup.
    Clicking on it will reset the state of the columns in the DataGrid, by clearing
    the localStorage data related to this column.
    The goal of this is to solve the problem of devextreme error 1047, when a
    previously existing column was selected but doesn't exist anymore.
    This is dirty, it uses classes from devextreme that might change style / behaviour.
    But dx doesn't seem to plan on solving this error, and I didn't manage to apply the
    workaround they proposed here https://supportcenter.devexpress.com/ticket/details/t974945/datagrid-the-e1047-error-occurs-when-loading-the-grid-state-with-an-extra-column-in-the
    (which is even dirtier anyway I would say).
  */

  const toolbar = document.querySelector(".dx-popup-normal .dx-toolbar-center");
  const maybeUndo = document.querySelector(".undo-columns-container");

  if (!toolbar.contains(maybeUndo)) {
    // Only add it if it doesn't exist
    const container = document.createElement("div");
    container.classList = "dx-item dx-toolbar-item dx-toolbar-button undo-columns-container";
    const undoButton = document.createElement("i");
    undoButton.classList = "dx-icon dx-icon-refresh";
    container.addEventListener("click", makeOnClickRefreshColumns(storageKey));
    container.appendChild(undoButton);
    toolbar.appendChild(container);
  }
}

function makeOnClickRefreshColumns(storageKey) {
  return async function () {
    if (!document.querySelector(".vex-dialog-message")) {
      const refreshConfirmed = await Dialog.okCancel(
        getUITranslation("Attention"),
        getUITranslation("Voulez-vous réinitialiser les colonnes affichées pour cette table ?")
      );

      if (refreshConfirmed) {
        localStorage.removeItem(storageKey);
        location.reload();
      }
    }
  };
}
export function sessionUrlCellTemplate(container, options) {
  let shortVersion = _.truncate(options.displayValue, {
    length: 60,
  });
  if (options.row.data.training_session_archived) {
    $(`<span>${shortVersion}</span>`).appendTo(container);
  } else {
    let url = `/training_sessions/${options.row.data.training_session_id || options.row.data.id}`;
    url = options.row.data.academy_id ? url + `?from_academy_id=${options.row.data.academy_id}` : url;
    urlCellTemplate(url, shortVersion, container);
  }
}

export function programCellTemplate(container, options) {
  if (options.displayValue) {
    let url = `/programs/${options.row.data.program_id}/edit`;
    url = options.row.data.academy_id ? url + `?from_academy_id=${options.row.data.academy_id}` : url;
    const title = options.displayValue;
    urlCellTemplate(url, title, container);
  }
}

export function dateCellTemplate(container, options) {
  if (options.displayValue) {
    let date = options.displayValue.toLocaleString("fr", { year: "numeric", month: "numeric", day: "numeric" });
    $(`<span>${date}</span>`).appendTo(container);
  }
}

const percentFormater = new Intl.NumberFormat(window._locale, { style: "percent" });

export function saleMarginRate({ revenue, charge }) {
  // marginRate expects an object with revenue and charges as keys
  let result = 0;
  if (revenue > 0) {
    result = (revenue - charge) / revenue;
  }
  return result == Infinity || result == -Infinity || Number.isNaN(result) ? 0 : result;
}

export function marginRate({ revenue, charge }) {
  // marginRate expects an object with revenue and charges as keys
  let result = 0;
  if (charge > 0) {
    result = (revenue - charge) / charge;
  }
  return result == Infinity || result == -Infinity || Number.isNaN(result) ? 0 : result;
}

export function saleMarginCellTemplate(container, options) {
  container.innerHTML = `<span>${percentFormater.format(saleMarginRate(options.value))}</span>`;
}

export function marginCellTemplate(container, options) {
  container.innerHTML = `<span>${percentFormater.format(marginRate(options.value))}</span>`;
}

export function calculateCustomSummaryMarginRate(options) {
  if (options.summaryProcess == "start") {
    options.totalValue = { revenue: 0, charge: 0 };
  }
  if (options.summaryProcess == "calculate") {
    options.totalValue.revenue += options.value.revenue;
    options.totalValue.charge += options.value.charge;
  }
  if (options.summaryProcess == "finalize") {
    if (options.name == "margin_rate") {
      options.totalValue = percentFormater.format(marginRate(options.totalValue));
    } else {
      options.totalValue = percentFormater.format(saleMarginRate(options.totalValue));
    }
  }
}

export function circleChartTemplate(container, options) {
  const { in_progress, total, text, color_filled, color_to_filled } = options;
  const percent = in_progress / total;
  const angle = Math.PI * 2 * 5 * percent;
  const html = `
  <div style="display: flex; align-items: center;">
    <svg width="20" height="20" style="margin-right: 5px">
        <circle r="10" cx="10" cy="10" fill=${color_filled} />
        <circle r="5" cx="10" cy="10" fill="transparent" stroke=${color_to_filled}  stroke-width="10" stroke-dasharray="${angle} 180" transform="rotate(-90, 10, 10)"/>
    </svg>
    <span style="font-size: 0.7em;">${text}</span>
  </div>`;

  $(html).appendTo(container);
}
/**
 *
 * @deprecated Prefer passing `toolbar: { visible: false }` to the DataGrid.
 */
export function maybeRemoveHeader(container) {
  container.element.querySelector(".dx-datagrid-header-panel")?.remove();
}
